import {
  BoxGeometry,
  BufferGeometry,
  Color,
  Fog,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
  type Material,
} from 'three';
import type { LevelRun } from '../core/level-runner';
import { isBlinkVisible, isInvulnerable } from '../core/lives';
import { pitchDegrees } from '../core/physics';
import { DERRIBO_TICKS } from '../core/level-state';
import { JEFE_1 } from '../data/boss-objects';
import { GROUND_Y, SIM_HZ, VIEW_WIDTH, WORLD_TOP } from '../data/tuning';
import { createHitboxOverlay, type HitboxOverlay } from './hitbox-overlay';
import { buildCrashFx } from './models/crash-fx';
import { buildLaChiva } from './models/la-chiva';
import { buildGallinazo, type Gallinazo } from './models/gallinazo';
import { buildHitmanPlane, type HitmanPlane } from './models/hitman-plane';
import { buildCableCar, type CableCarModel } from './models/cable-car';
import { buildHippo, type Hippo } from './models/hippo';
import { buildParaglider, type Paraglider } from './models/paraglider';
import { buildCropDuster, type CropDusterModel } from './models/crop-duster';
import { buildLawHelicopter, type LawHelicopter } from './models/law-helicopter';
import { buildPowerUpModels } from './models/powerups';
import { createSeagullFactory, type Seagull } from './models/seagull';
import { buildAntenna, type AntennaModel } from './models/antenna';
import { flockMembers } from '../core/enemies/seagull-flock';
import { buildPortCrane, type PortCrane } from './models/port-crane';
import { craneView } from '../core/enemies/port-crane';
import { RETREAT_TICKS } from '../core/boss/boss-fight';
import { buildJumpProps } from './jump/models';
import { buildJefe2, HOOF_OFFSET } from './boss/jefe-2';
import { jefe2Animation, legSwing, type Jefe2Animation } from './boss/jefe-2-view';
import { buildWallPool } from './boss/portrait-walls';
import { buildJefe3 } from './boss/jefe-3';
import { buildJefe4 } from './boss/jefe-4';
import { jefe4Animation, isFigureVisible, retreatPhase, type Jefe4Animation } from './boss/jefe-4-view';
import { buildBoardPool } from './boss/jefe-3-board';
import { balloonScale, capoAnimation, jefe3Animation, phoneAnimation, type CapoAnimation, type Jefe3Animation, type PhoneAnimation } from './boss/jefe-3-view';
import { emptyPlanePitch, jumpVisibility } from './jump/jump-view';
import { buildAttackPool } from './boss/attack-objects';
import { bossPose, easeOut, retreatProgress } from './boss/boss-pose';
import { buildJefe1, SKID_OFFSET } from './boss/jefe-1';
import { createBagSwing, stepBagSwing } from './models/bag-swing';
import { viewOf } from '../core/level-runner';
import { collectRenderedObjects, type RenderedObject } from './scene-info';
import { CAMERA_DISTANCE, FOV_DEGREES, REDUCED_PARALLAX, cameraXFor, lerp, toRadians } from './scene-math';
import { playerViewModel } from './player-view';
import { applyTheme, type AppliedTheme, type WorldTheme } from './themes/theme';
import { themeById, themeFor, usSkyThemeFor } from './themes';
import { registerRenderer, releaseRenderer } from './renderer-registry';
import { buildColumnBatches } from './skins/instanced';
import { attachView, type ViewInfo } from './view';
import { InstancedBatch } from './pools/instanced-batch';
import { ObjectPool } from './pools/object-pool';

/**
 * Greybox renderer of a level (issue #53, GDD §14.1, §16.4): primitives with flat shading and neutral
 * greys, a fixed side camera and one named object per core entity. It only reads the core (GDD §15.2).
 * Objects live in absolute level coordinates; the camera follows the plane so that it stays at 30 % of the
 * view width. The plane is drawn between the previous and the current tick with `alpha` (GDD §15.4).
 */

const GROUND_WIDTH = 80;
const COLUMN_DEPTH = 1;
/** Theme crossfade at the border (GDD §11.1, #97): 2 s (120 ticks); instant with reduced motion. */
const THEME_TRANSITION_TICKS = 120;
const CHECKPOINT_HEIGHT = 5;
const PROPELLER_RAD_PER_S = 40;

export type { RenderedObject };

/** Visibility and altitude of the objects of the player at the end of each tick, for the test hooks (issue #67). */
export interface TraceEntry {
  tick: number;
  state: string;
  objects: Record<string, { visible: boolean; y: number }>;
}

export interface RenderInfo extends ViewInfo {
  objects: RenderedObject[];
  /** The last 600 ticks. */
  trace: TraceEntry[];
  drawCalls: number;
  triangles: number;
  frame: { calls: number; triangles: number };
  maxFrame: { calls: number; triangles: number };
  sceneObjectCount: number;
  columnBoundsChecks?: { checked: number; failures: string[] };
  decorationCounts?: Record<string, number>;
  /** The pose of the boss model being shown (JEFE-2, issue #111), else null. */
  boss: { animation: string; visible: boolean; phone?: string; capo?: string } | null;
  /** Camera shakes started so far (the retreat of a boss shakes it once, never with reduced motion; issue #96). */
  cameraShakes: number;
  /** Full-screen flashes triggered so far: the game has none (GDD §13.6). */
  flashes: number;
  /** Screen position of the world layers (view units at the play plane); it moves by the parallax of each one (#139). */
  layerScreenX: Record<'far' | 'mid', number>;
  /** Every object name that has been in the scene, to check what was never added (no crash or explosion). */
  addedNames: string[];
  /**
   * The active world theme (issue #68): id, variant, its declared sky and fog colours, and the sky and fog
   * actually applied to the scene (they must match).
   */
  theme: { id: string; variant: string; sky: number; fog: number; background: number; sceneFog: number | null };
  /** An in-progress theme crossfade at the border (#97): the outgoing variant and the ticks left, else null. */
  themeTransition: { from: string; ticksLeft: number } | null;
  /** Live GPU counters of the renderer, to check that switching themes leaks nothing (issue #68). */
  memory: { geometries: number; textures: number };
}

export interface LevelScene {
  readonly canvas: HTMLCanvasElement;
  readonly hitboxesOn: boolean;
  /** Keeps the state of the plane before a simulation step, for the interpolation and the swing of the bag. */
  beforeStep(): void;
  /** Called after the step: La Bolsa reacts to the change of vertical speed. */
  afterStep(): void;
  render(alpha: number): void;
  setHitboxes(on: boolean): void;
  /** Debug (outside production): applies a theme by id and variant, or throws naming an unknown one (issue #68). */
  requestTheme(id: string, variant?: string): void;
  /** Opaque coverage (0 to 1) of a column piece over its collision AABB, for the silhouette scenario (#69). */
  coverage(objectPath: string): number;
  /** World bounding boxes (min/max) of the decorative parts of a column piece, for the containment scenario (#69). */
  columnParts(objectPath: string): { min: [number, number, number]; max: [number, number, number] }[];
  info(): RenderInfo;
  /** Lightweight counters/theme snapshot; no geometry traversal on each simulation tick. */
  summary(): Pick<RenderInfo, 'theme' | 'themeTransition' | 'memory'>;
  markFrame(): number;
  maxSince(marker: number): { calls: number; triangles: number };
  dispose(): void;
}

export interface LevelSceneOptions {
  hitboxes?: boolean;
  /** The Reduced motion setting, read every frame: it replaces the blink with a translucent model. */
  reducedMotion?: () => boolean;
}

/** The pad of La Catedral stands this far ahead of the boss when it starts to retreat (u). */
const RETREAT_PAD_AHEAD = 17;
/** How far the hanging plush hippo is reeled up while landing (u): its bottom ends level with the skids. */
const PLUSH_REEL = 1.05;

type EnemyHelicopter = LevelRun['enemies'][number]['helicopter'];
const NO_HELICOPTER = { arrivalLeft: 1, angle: 0, searchlight: 'idle', projectiles: [] } as unknown as NonNullable<EnemyHelicopter>;

const grey = (color: number) => new MeshStandardMaterial({ color, flatShading: true });

export function createLevelScene(container: HTMLElement, run: LevelRun, options: LevelSceneOptions = {}): LevelScene {
  const renderer = registerRenderer(new WebGLRenderer({ antialias: true }));
  renderer.info.autoReset = false;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.domElement.dataset.testid = 'game-canvas';
  container.append(renderer.domElement);

  const scene = new Scene();

  const camera = new PerspectiveCamera(FOV_DEGREES, 16 / 9, 1, 400);
  camera.position.set(cameraXFor(run.core.player.x), WORLD_TOP / 2, CAMERA_DISTANCE);
  camera.lookAt(camera.position.x, WORLD_TOP / 2, 0);

  // World theme (issue #68): sky, lights, ground, ceiling line and the three parallax layers. The level
  // picks its variant by world and time of day; the palette is game art (GDD §14.1, §14.2).
  const themeOptions = {
    groundWidth: GROUND_WIDTH,
    groundY: GROUND_Y,
    worldTop: WORLD_TOP,
    cameraDistance: CAMERA_DISTANCE,
    viewWidth: VIEW_WIDTH,
    levelLength: run.level.length,
  };
  const themeDef = themeFor(run.level.def.world, run.level.def.timeOfDay);
  let theme: AppliedTheme = applyTheme(scene, themeDef, themeOptions);

  // Theme transition (#97): when the plane crosses the border the US sky theme crossfades in. The old
  // theme fades out over TRANSITION_TICKS (or instantly with reduced motion, GDD §13.6), then is disposed.
  const transitionTicks = () => (options.reducedMotion?.() ? 1 : THEME_TRANSITION_TICKS);
  const fromSky = new Color();
  const toSky = new Color();
  const fromFog = new Color();
  const toFog = new Color();
  const mix = new Color();
  let themeTransition: { from: AppliedTheme; to: AppliedTheme; total: number; elapsed: number } | null = null;
  let sawUsSky = run.usSky !== null;
  const beginThemeTransition = (to: WorldTheme) => {
    if (theme.info().id === to.id && theme.info().variant === to.variant) return;
    fromSky.setHex(theme.info().sky);
    fromFog.setHex(theme.info().fog);
    toSky.setHex(to.sky);
    toFog.setHex(to.fog.color);
    const next = applyTheme(scene, to, themeOptions);
    next.setOpacity(0);
    themeTransition = { from: theme, to: next, total: transitionTicks(), elapsed: 0 };
    theme = next;
  };
  const stepThemeTransition = () => {
    if (!themeTransition) return;
    const { from, to, total } = themeTransition;
    themeTransition.elapsed += 1;
    const t = Math.min(1, themeTransition.elapsed / total);
    from.setOpacity(1 - t);
    to.setOpacity(t);
    // The scene background and fog are not part of either theme's group: interpolate them by hand, so the
    // sky never jumps (no full-screen flash, #97).
    (scene.background as Color).copy(mix.copy(fromSky).lerp(toSky, t));
    if (scene.fog instanceof Fog) scene.fog.color.copy(mix.copy(fromFog).lerp(toFog, t));
    if (t >= 1) {
      from.dispose();
      themeTransition = null;
    }
  };

  const materials = {
    dollar: grey(themeDef.collidable.pickup),
    pole: grey(0xb0b0b0),
    flagRed: grey(0x9a5a5a),
    flagGreen: grey(0x5a9a6a),
  };
  const geometries: BufferGeometry[] = [];
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  // La Chiva and La Bolsa (issue #66), shared with the title screen.
  const chiva = buildLaChiva();
  const player = chiva.group;
  scene.add(player);
  // The comic crash (GDD §3.4): smoke, a pilot and a parachute, shown only in DERRIBO.
  const fx = buildCrashFx();
  scene.add(fx.smoke, fx.pilot, fx.parachute);

  // Gap columns dressed by their skin (#69): the visual bounds of every piece equal its collision AABB
  // (GDD §14.1). The skin comes from the level data; the greybox `test` and the worlds without art yet
  // fall back to a saturated box, so what collides is always what is seen.
  const columns = buildColumnBatches(run, COLUMN_DEPTH, themeDef.collidable.column);
  scene.add(columns.group);

  const dollarCount = run.level.pickups.filter((pickup) => pickup.type === 'dollar').length;
  const dollarMesh = new InstancedMesh(own(new IcosahedronGeometry(0.3, 0)), materials.dollar, dollarCount);
  dollarMesh.name = 'pickups.dollar.instanced';
  dollarMesh.frustumCulled = false;
  const dollars = new InstancedBatch(dollarMesh, dollarCount);
  dollarMesh.userData.instanceIds = dollars.names;
  scene.add(dollarMesh);
  const dollarMatrix = new Matrix4();
  const updateDollars = () => {
    // Release before acquiring: a full batch can replace all ids without exceeding its capacity.
    for (let slot = dollars.names.length - 1; slot >= 0; slot--) {
      const id = dollars.names[slot]!;
      const index = Number(id.slice('dollar:'.length));
      if (run.collectedPickups.has(index) || !run.entities.some((entity) => entity.kind === 'pickup' && entity.index === index && entity.active)) dollars.release(id);
    }
    for (const entity of run.entities) {
      if (entity.kind !== 'pickup' || !entity.active || run.collectedPickups.has(entity.index)) continue;
      const pickup = run.level.pickups[entity.index]!;
      if (pickup.type !== 'dollar') continue;
      dollarMatrix.makeRotationY(options.reducedMotion?.() ? 0 : run.core.tick / SIM_HZ);
      dollarMatrix.setPosition(pickup.x, pickup.y, 0);
      dollars.set(`dollar:${entity.index}`, dollarMatrix);
    }
  };

  // Power-ups (#82): the pickups in the scene and the states of La Chiva, added and removed with the core state.
  const powerUps = buildPowerUpModels();
  scene.add(powerUps.armorPlates, powerUps.discoLights);
  powerUps.armorPlates.visible = false;
  powerUps.discoLights.visible = false;
  const powerUpPickups = new Map<number, Group>();
  run.level.pickups.forEach((pickup, index) => {
    if (pickup.type === 'dollar') return;
    const model = powerUps.pickup(pickup.type);
    model.position.set(pickup.x, pickup.y, 0);
    powerUpPickups.set(index, model);
    scene.add(model);
  });

  const flags = new Map<string, Mesh>();
  for (const checkpoint of run.checkpoints.checkpoints) {
    const group = new Group();
    group.name = `checkpoint:${checkpoint.id}`;
    const pole = new Mesh(own(new BoxGeometry(0.1, CHECKPOINT_HEIGHT, 0.1)), materials.pole);
    pole.position.y = GROUND_Y + CHECKPOINT_HEIGHT / 2;
    const flag = new Mesh(own(new BoxGeometry(0.9, 0.5, 0.08)), materials.flagRed);
    flag.position.set(0.5, GROUND_Y + CHECKPOINT_HEIGHT - 0.3, 0);
    group.add(pole, flag);
    group.position.x = checkpoint.x;
    flags.set(checkpoint.id, flag);
    scene.add(group);
  }

  // EN-01 (#71/#70): fixed pool of groups sharing the prototype's GPU geometry and materials.
  const enemyModels = new Map<number, Gallinazo>();
  const birdCount = run.enemies.filter((enemy) => enemy.type === 'EN-01').length;
  const birdPrototype = birdCount > 0 ? buildGallinazo() : null;
  let birdSlot = 0;
  const birdPool = new ObjectPool<Gallinazo>(birdCount, () => {
    const group = birdPrototype!.group.clone(true);
    group.name = `enemy-pool:EN-01:${birdSlot++}`;
    group.userData.poolName = group.name;
    group.visible = false;
    scene.add(group);
    return { group, wings: [group.children[4] as Mesh, group.children[5] as Mesh] as [Mesh, Mesh], dispose: () => undefined };
  });
  const updateBirdPool = (active: Set<number>) => {
    for (const [index, model] of enemyModels) {
      if (active.has(index)) continue;
      model.group.visible = false;
      model.group.name = model.group.userData.poolName as string;
      enemyModels.delete(index);
      birdPool.release(model);
    }
    for (const enemy of run.enemies) {
      if (enemy.type !== 'EN-01' || !active.has(enemy.index) || enemyModels.has(enemy.index)) continue;
      const model = birdPool.acquire();
      model.group.name = `enemies.${enemy.type}.${enemy.index}`;
      enemyModels.set(enemy.index, model);
    }
  };
  // EN-23 (#106): three seagulls per flock, where the core puts the leader and its wingmen.
  const seagulls = createSeagullFactory();
  const flockModels = new Map<number, { group: Group; birds: Seagull[] }>();
  for (const enemy of run.enemies) {
    if (enemy.type !== 'EN-23') continue;
    const group = new Group();
    group.name = `enemies.${enemy.type}.${enemy.index}`;
    const birds = [0, 1, 2].map((member) => seagulls.build(`enemy:EN-23:${enemy.index}:${member}`));
    for (const bird of birds) group.add(bird.group);
    flockModels.set(enemy.index, { group, birds });
    scene.add(group);
  }
  // EN-22 (#105): the antenna with its zone, and the EN-03 it calls once it has taken off.
  const antennaModels = new Map<number, { antenna: AntennaModel; hitman: HitmanPlane }>();
  for (const enemy of run.enemies) {
    if (!enemy.antenna) continue;
    const antenna = buildAntenna(`enemies.${enemy.type}.${enemy.index}`);
    antenna.group.position.set(enemy.x, enemy.y, 0);
    const hitman = buildHitmanPlane();
    hitman.group.name = `enemies.EN-22.${enemy.index}.hitman`;
    hitman.group.visible = false;
    antennaModels.set(enemy.index, { antenna, hitman });
    scene.add(antenna.group, hitman.group);
  }
  const hitmanModels = new Map<number, HitmanPlane>();
  for (const enemy of run.enemies) {
    if (enemy.type !== 'EN-03') continue;
    const model = buildHitmanPlane();
    model.group.name = `enemies.${enemy.type}.${enemy.index}`;
    model.group.visible = false;
    hitmanModels.set(enemy.index, model);
    scene.add(model.group);
  }
  // EN-02 (#84): the helicopter, its searchlight cone and a small pool of projectiles; the cone and the
  // projectiles live in the scene (not under the helicopter) because the cone turns and the shots fly free.
  const helicopterModels = new Map<number, LawHelicopter>();
  for (const enemy of run.enemies) {
    if (!enemy.helicopter) continue;
    const model = buildLawHelicopter(WORLD_TOP);
    model.group.name = `enemies.${enemy.type}.${enemy.index}`;
    model.group.visible = false;
    model.cone.visible = false;
    helicopterModels.set(enemy.index, model);
    scene.add(model.group, model.cone, ...model.projectiles);
  }
  // EN-12 (#88): a pale cable with two towers and the saturated cabin that moves along it.
  const cableCarModels = new Map<number, CableCarModel>();
  for (const enemy of run.enemies) {
    if (!enemy.cableCar) continue;
    const def = run.level.enemies[enemy.index]!;
    const model = buildCableCar();
    model.setCable(def.x, def.y, def.params?.x2 ?? def.x, def.params?.y2 ?? def.y);
    model.cabin.visible = false;
    cableCarModels.set(enemy.index, model);
    scene.add(model.cable, model.towers, model.cabin);
  }
  // EN-11 (#87): a lagoon on the ground with bubbles, and the hippo that jumps out of it.
  // EN-31 (#116): the crop duster and the pool of puffs of its toxic cloud.
  const dusterModels = new Map<number, CropDusterModel>();
  for (const enemy of run.enemies) {
    if (!enemy.cropDuster) continue;
    const model = buildCropDuster();
    model.group.name = `enemies.${enemy.type}.${enemy.index}`;
    dusterModels.set(enemy.index, model);
    scene.add(model.group, ...model.puffs);
  }
  const hippoModels = new Map<number, Hippo>();
  for (const enemy of run.enemies) {
    if (!enemy.hippo) continue;
    const model = buildHippo();
    model.lagoon.position.set(enemy.x, GROUND_Y + 0.02, 0.1);
    model.bubbles.position.set(enemy.x, GROUND_Y + 0.1, 0);
    model.body.position.set(enemy.x, GROUND_Y, 0);
    hippoModels.set(enemy.index, model);
    scene.add(model.lagoon, model.bubbles, model.body);
  }
  // EN-04 (#86): the rider hangs below its canopy; once landed the canopy is gone and the rider walks off.
  const paragliderModels = new Map<number, Paraglider>();
  for (const enemy of run.enemies) {
    if (!enemy.paraglider) continue;
    const model = buildParaglider();
    model.group.name = `enemies.${enemy.type}.${enemy.index}`;
    model.group.visible = false;
    paragliderModels.set(enemy.index, model);
    scene.add(model.group);
  }
  // EN-21 (#104): the port crane, with its arm turned and its beacon lit as the core says.
  const craneModels = new Map<number, PortCrane>();
  for (const enemy of run.enemies) {
    if (!enemy.crane) continue;
    const model = buildPortCrane(`enemies.${enemy.type}.${enemy.index}`, enemy.x, enemy.y);
    craneModels.set(enemy.index, model);
    scene.add(model.group);
  }
  // JEFE-1 (#96): the helicopter with the boss and the plush hippo, the La Catedral set of the retreat, the pool of
  // attacks of any boss and the law helicopter of phase 3 (the EN-02 model of #84).
  const jefe1 = run.boss?.def.id === 'JEFE-1' ? buildJefe1() : null;
  if (jefe1) {
    jefe1.helicopter.visible = false;
    scene.add(jefe1.helicopter, jefe1.retreatSet);
  }
  // JEFE-2 (#111): the rider on the winged horse, the parade of the retreat and the portrait walls of the final phase.
  const jefe2 = run.boss?.def.id === 'JEFE-2' ? buildJefe2() : null;
  const wallPool = jefe2 ? buildWallPool() : null;
  if (jefe2 && wallPool) scene.add(jefe2.root, jefe2.parade, wallPool.group);
  // JEFE-3 (#122): the desk on the balloon, the board with its pieces and highlights, the check columns and the called minibosses.
  const jefe3 = run.boss?.def.id === 'JEFE-3' ? buildJefe3() : null;
  const boardPool = jefe3 ? buildBoardPool() : null;
  if (jefe3 && boardPool) scene.add(jefe3.root, boardPool.group);
  // JEFE-4 (#134): the figure that pops out of the tunnels, the trapdoors, the dust, the motorbike and the cell of the retreat.
  const jefe4 = run.boss?.def.id === 'JEFE-4' ? buildJefe4() : null;
  if (jefe4) scene.add(jefe4.root, jefe4.moto, jefe4.trapdoorFloor, jefe4.trapdoorCeiling, jefe4.dust, jefe4.cell);
  let jefe4View: { animation: Jefe4Animation; visible: boolean } | null = null;
  let jefe3View: { animation: Jefe3Animation; phone: PhoneAnimation; capo: CapoAnimation; visible: boolean } | null = null;
  let seenRings = 0;
  let lastRingTick: number | null = null;
  let jefe2View: { animation: Jefe2Animation; visible: boolean } | null = null;
  let seenHorseshoes = 0;
  let lastThrowTick: number | null = null;
  const attackPool = run.attacks ? buildAttackPool() : null;
  if (attackPool) scene.add(attackPool.group);
  const bossLaw = run.attacks && jefe1 ? buildLawHelicopter(WORLD_TOP) : null;
  if (bossLaw) {
    bossLaw.group.name = 'boss-jefe-1-law-helicopter';
    bossLaw.group.visible = false;
    bossLaw.cone.visible = false;
    scene.add(bossLaw.group, bossLaw.cone, ...bossLaw.projectiles);
  }
  // El Salto (#94): the border sign, the X on the ground and the pilot that falls, floats and lands.
  const jumpProps = buildJumpProps();
  scene.add(jumpProps.sign, jumpProps.target, jumpProps.pilotFalling, jumpProps.parachute, jumpProps.pilotLanded);
  let lastPlaneY = run.core.player.y;
  let retreatFrom: { x: number; y: number } | null = null;
  let cameraShakes = 0;
  let shakeUntil = -1;
  let lastBossStatus = run.boss?.status ?? null;
  const addedNames = new Set<string>();
  const activeEnemies = () =>
    new Set(run.entities.filter((entity) => entity.kind === 'enemy' && entity.active).map((entity) => entity.index));

  const view = attachView(container, renderer, camera);

  let previous = { x: run.core.player.x, y: run.core.player.y, vy: run.core.player.vy };
  let swing = createBagSwing();
  let lastRender = performance.now();
  let overlay: HitboxOverlay | null = null;
  const setHitboxes = (on: boolean) => {
    if (on && !overlay) {
      overlay = createHitboxOverlay(run);
      scene.add(overlay.group);
    } else if (!on && overlay) {
      scene.remove(overlay.group);
      overlay.dispose();
      overlay = null;
    }
  };
  if (options.hitboxes) setHitboxes(true);

  // Debug (#68): re-applies a theme by id, disposing the previous one so the counters stay put. An unknown
  // id throws before anything changes.
  const requestTheme = (id: string, variant?: string) => {
    const next = themeById(id, variant);
    theme.dispose();
    theme = applyTheme(scene, next, themeOptions);
  };

  const modelParts = Object.values(chiva.parts);
  const modelMaterials = new Set<{ opacity: number; transparent: boolean }>();
  player.traverse((child) => {
    if (child instanceof Mesh) modelMaterials.add(child.material as { opacity: number; transparent: boolean });
  });
  const TRACKED = ['player.airframe', 'player.bag', 'player.smoke', 'player.pilot', 'player.parachute'] as const;
  const trace: TraceEntry[] = [];
  let ejected: { y: number; tick: number } | null = null;

  /** Puts the plane, its states and its fx where the core says (alpha 1 is the current tick). */
  const applyView = (alpha: number) => {
    const { core } = run;
    const state = core.player;
    const x = lerp(previous.x, state.x, alpha);
    const y = lerp(previous.y, state.y, alpha);
    const derriboTicks = core.levelState === 'DERRIBO' ? DERRIBO_TICKS - run.derriboRemaining : 0;
    const model = playerViewModel({
      levelState: core.levelState,
      derriboTicks,
      invulnerable: isInvulnerable(core),
      blinkVisible: isBlinkVisible(core),
      reducedMotion: options.reducedMotion?.() ?? false,
    });
    // After the jump `core.player` is the pilot: the plane is the empty one at `planeX` (#94).
    const jumping = run.jump !== null;
    if (!jumping) lastPlaneY = y;
    player.position.set(jumping ? (run.planeX ?? x) : x, jumping ? lastPlaneY : y, 0);
    player.name = jumping ? 'player-plane' : 'player';
    // The falling plane tumbles; otherwise the pitch follows the vertical speed; the empty plane bobs, never smokes.
    const pitch = pitchDegrees(lerp(previous.vy, state.vy, alpha));
    player.rotation.z = jumping
      ? toRadians(emptyPlanePitch(run.jump!.ticks, options.reducedMotion?.() ?? false))
      : toRadians(pitch - derriboTicks * 8);
    chiva.parts.bag.rotation.z = swing.angle;
    for (const part of modelParts) part.visible = model.airframeVisible;
    for (const material of modelMaterials) {
      material.opacity = model.opacity;
      material.transparent = model.opacity < 1;
    }
    fx.smoke.visible = model.smoke;
    fx.smoke.position.set(x, y, 0.2);
    // The pilot jumps out where the plane is and floats down under the parachute.
    if (model.pilot) ejected ??= { y, tick: derriboTicks };
    else ejected = null;
    fx.pilot.visible = model.pilot;
    fx.parachute.visible = model.parachute;
    if (ejected) {
      const pilotY = ejected.y + 0.4 - ((derriboTicks - ejected.tick) / SIM_HZ) * 1.2;
      fx.pilot.position.set(x + 0.3, pilotY, 0.1);
      fx.parachute.position.set(x + 0.3, pilotY + 0.25, 0.1);
    }
    // In the jump zone the view is frozen: the camera, the ground and the theme stay where the core froze them.
    const cameraX = run.jumpZone ? run.jumpZone.viewLeft + VIEW_WIDTH / 2 : cameraXFor(x);
    theme.follow(cameraX, WORLD_TOP / 2, options.reducedMotion?.() ? REDUCED_PARALLAX : 1);
    camera.position.x = cameraX;
    const showJump = jumpVisibility({
      usSky: run.usSky !== null,
      zone: run.jumpZone !== null,
      jump: run.jump?.phase ?? null,
      planeX: run.planeX,
      viewRight: viewOf(run).right,
    });
    jumpProps.sign.visible = showJump.borderSign;
    if (run.usSky) jumpProps.sign.position.set(run.usSky.startX + 4, GROUND_Y, -1);
    jumpProps.target.visible = showJump.target;
    if (run.jumpZone) jumpProps.target.position.set(run.jumpZone.targetX, GROUND_Y, 0.05);
    jumpProps.pilotFalling.visible = showJump.pilotFalling;
    jumpProps.pilotFalling.position.set(x, y, 0.1);
    jumpProps.parachute.visible = showJump.parachute;
    jumpProps.parachute.position.set(x, y, 0.1);
    jumpProps.pilotLanded.visible = showJump.pilotLanded;
    jumpProps.pilotLanded.position.set(x, GROUND_Y, 0.1);
    player.visible = showJump.plane;
    // The retreat of a boss shakes the camera for a moment; never with reduced motion (it is not even started).
    camera.position.y = WORLD_TOP / 2 + (core.tick < shakeUntil ? Math.sin(core.tick * 2.1) * 0.06 : 0);
    camera.lookAt(camera.position.x, WORLD_TOP / 2, 0);
    updateDollars();
    columns.update();
    // Pickups bob gently (still with reduced motion); the hitbox stays where the core puts it.
    const steady = options.reducedMotion?.() ?? false;
    const seconds = performance.now() / 1000;
    for (const [index, pickupModel] of powerUpPickups) {
      const pickup = run.level.pickups[index]!;
      pickupModel.visible = !run.collectedPickups.has(index);
      pickupModel.position.y = pickup.y + (steady ? 0 : Math.sin(seconds * 2) * 0.08);
      pickupModel.rotation.y = steady ? 0 : seconds;
    }
    // The plates are bolted on while armored and the disco lights shine while the bribe lasts.
    const attach = (object: Group, on: boolean) => {
      object.visible = on;
      if (on && object.parent !== player) player.add(object);
      else if (!on && object.parent === player) scene.add(object);
    };
    attach(powerUps.armorPlates, core.armor && model.airframeVisible);
    attach(powerUps.discoLights, core.bribeRemaining > 0 && model.airframeVisible);
    powerUps.updateDisco(seconds, steady);
    const active = activeEnemies();
    updateBirdPool(active);
    // Decorative wing beat at 3 Hz; still with reduced motion (GDD §13.6).
    const beat = options.reducedMotion?.() ? 0 : Math.sin((performance.now() / 1000) * 2 * Math.PI * 3) * 0.5;
    for (const [index, model] of enemyModels) {
      const enemy = run.enemies[index]!;
      model.group.position.set(enemy.x, enemy.y, 0);
      model.group.visible = active.has(index);
      model.wings[0].rotation.x = beat;
      model.wings[1].rotation.x = -beat;
    }
    for (const [index, model] of antennaModels) {
      const state = run.enemies[index]!.antenna!;
      model.antenna.group.visible = active.has(index);
      model.antenna.setTriggered(state.phase !== 'IDLE');
      model.hitman.group.visible = state.hitman !== null;
      if (state.hitman) model.hitman.group.position.set(state.hitman.x, state.hitman.y, 0);
      model.hitman.propeller.rotation.x += 0.7;
    }
    for (const [index, flock] of flockModels) {
      const enemy = run.enemies[index]!;
      flock.group.visible = active.has(index);
      flockMembers(enemy.x, enemy.y, run.level.enemies[index]!.params).forEach((member, at) => {
        const bird = flock.birds[at]!;
        bird.group.position.set(member.x, member.y, 0);
        bird.wings[0].rotation.x = beat;
        bird.wings[1].rotation.x = -beat;
      });
    }
    for (const [index, model] of cableCarModels) {
      const enemy = run.enemies[index]!;
      const near = active.has(index);
      model.cabin.visible = near;
      model.cabin.position.set(enemy.x, enemy.y, 0);
      model.cable.visible = near;
      model.towers.visible = near;
    }
    for (const [index, model] of dusterModels) {
      const enemy = run.enemies[index]!;
      model.group.position.set(enemy.x, enemy.y, 0);
      model.group.visible = active.has(index);
      // The cloud stays where it was sprayed, even when the plane has left; it swells a little as it ages (still with reduced motion).
      const cloud = enemy.cropDuster!.puffs;
      model.puffs.forEach((puff, at) => {
        const sprayed = cloud[at];
        puff.visible = sprayed !== undefined;
        if (sprayed) {
          puff.position.set(sprayed.x, sprayed.y, 0);
          puff.scale.setScalar(0.8 + 0.2 * Math.min(1, sprayed.age / 60));
        }
      });
    }
    for (const [index, model] of hippoModels) {
      const enemy = run.enemies[index]!;
      const phase = enemy.hippo!.phase;
      const near = active.has(index);
      model.lagoon.visible = near;
      model.bubbles.visible = near && phase === 'bubbling';
      model.body.visible = near && phase === 'airborne';
      model.body.position.set(enemy.x, enemy.y, 0);
      // The bubbles rise and pop with render time; they are decoration only.
      model.bubbles.children.forEach((bubble, at) => {
        bubble.position.y = 0.1 + ((performance.now() / 1000 + at * 0.3) % 0.8) * 0.6;
      });
    }
    for (const [index, model] of paragliderModels) {
      const enemy = run.enemies[index]!;
      model.group.position.set(enemy.x, enemy.y - 0.2, 0);
      model.group.visible = active.has(index);
      model.canopy.visible = !enemy.paraglider!.landed;
    }
    const placeHelicopter = (model: LawHelicopter, at: { x: number; y: number }, helicopter: NonNullable<EnemyHelicopter>, shown: boolean) => {
      model.group.position.set(at.x, at.y, 0);
      model.group.visible = shown;
      model.rotor.rotation.y += 0.9;
      // The cone only exists once the helicopter has arrived; it hangs down and turns with the sweep.
      model.cone.visible = shown && helicopter.arrivalLeft === 0;
      model.cone.position.set(at.x, at.y - 0.4, -0.1);
      model.cone.rotation.z = toRadians(helicopter.angle);
      model.setAmber(helicopter.searchlight === 'amber');
      const shots = helicopter.projectiles.filter((candidate) => candidate.delay === 0);
      model.projectiles.forEach((bullet, at) => {
        const shot = shots[at];
        bullet.visible = shot !== undefined;
        if (shot) bullet.position.set(shot.x, shot.y, 0);
      });
    };
    for (const [index, model] of helicopterModels) {
      const enemy = run.enemies[index]!;
      placeHelicopter(model, enemy, enemy.helicopter!, active.has(index));
    }
    // The boss fight (#96): helicopter pose, retreat to La Catedral, attacks and the law helicopter of phase 3.
    if (run.boss && jefe1) {
      const boss = run.boss;
      if (boss.status === 'entering') retreatFrom = null;
      if (boss.status === 'retreating' && lastBossStatus !== 'retreating' && !(options.reducedMotion?.() ?? false)) {
        cameraShakes += 1;
        shakeUntil = core.tick + 12;
      }
      lastBossStatus = boss.status;
      const pose = bossPose(boss.status, boss.retreatLeft);
      jefe1.helicopter.userData.pose = pose;
      jefe1.helicopter.visible = boss.status !== 'entering';
      const spin = 0.9 * (pose === 'landed' ? 0 : pose === 'retreat' ? 1 - retreatProgress(boss.retreatLeft, RETREAT_TICKS) : 1);
      jefe1.rotor.rotation.y += spin;
      jefe1.plush.rotation.z = steady ? 0 : Math.sin(seconds * 2.5) * 0.25;
      // The toy is reeled up as the helicopter lands, so nothing hangs below the skids on the pad.
      jefe1.plush.position.y = -SKID_OFFSET + PLUSH_REEL * easeOut(pose === 'retreat' || pose === 'landed' ? retreatProgress(boss.retreatLeft, RETREAT_TICKS) : 0);
      jefe1.arm.rotation.z = pose === 'transition' ? 2.3 : 0;
      jefe1.fuelSmoke.visible = pose === 'retreat';
      jefe1.helicopter.rotation.z = pose === 'transition' ? Math.sin(core.tick * 0.5) * 0.12 : 0;
      if (pose === 'retreat' || pose === 'landed') {
        retreatFrom ??= { x: boss.x, y: boss.y };
        // The pad of La Catedral is ahead of where the boss started to give up; it lands exactly on it.
        const padX = retreatFrom.x + RETREAT_PAD_AHEAD;
        jefe1.retreatSet.visible = true;
        jefe1.retreatSet.position.set(padX - jefe1.pad.position.x, GROUND_Y, 0);
        const padTop = GROUND_Y + jefe1.pad.position.y + 0.03;
        const t = easeOut(retreatProgress(boss.retreatLeft, RETREAT_TICKS));
        jefe1.helicopter.position.set(retreatFrom.x + (padX - retreatFrom.x) * t, retreatFrom.y + (padTop + SKID_OFFSET - retreatFrom.y) * t, 0);
      } else {
        jefe1.retreatSet.visible = false;
        jefe1.helicopter.position.set(boss.x, boss.y, 0);
      }
    }
    if (jefe2 && wallPool && run.boss && run.attacks) {
      const boss = run.boss;
      const horseshoes = run.attacks.spawnLog.filter((entry) => entry.type === 'horseshoe').length;
      if (horseshoes > seenHorseshoes) lastThrowTick = core.tick;
      seenHorseshoes = horseshoes;
      if (boss.status === 'entering') retreatFrom = null;
      const animation = jefe2Animation({
        status: boss.status,
        phaseName: boss.def.phases[boss.phase - 1]?.name ?? '',
        ticksSinceThrow: lastThrowTick === null ? null : core.tick - lastThrowTick,
        ticksSinceHit: run.decoys?.lastHitTick == null ? null : core.tick - run.decoys.lastHitTick,
      });
      jefe2View = { animation, visible: boss.status !== 'entering' };
      jefe2.root.visible = boss.status !== 'entering';
      jefe2.root.userData.pose = animation;
      // The propeller spins and the four-beat gait goes on with render time; both are information (still with reduced motion).
      jefe2.propeller.rotation.x += animation === 'RETREAT' && boss.status === 'done' ? 0 : 0.8;
      const cycle = animation === 'RETREAT' ? 0 : seconds * 1.5;
      jefe2.legs.forEach((leg, at) => (leg.rotation.z = legSwing(cycle, at)));
      jefe2.wings.rotation.x = steady ? 0 : Math.sin(seconds * 6) * 0.15;
      jefe2.arm.rotation.z = animation === 'THROW' ? -1.6 : animation === 'STAGGER' ? 2.4 : animation === 'RETREAT' ? 2.8 : 0;
      jefe2.root.rotation.z = animation === 'STAGGER' ? Math.sin(core.tick * 0.5) * 0.12 : animation === 'FLOUR_HIT' ? 0.2 : 0;
      jefe2.root.scale.y = animation === 'HIDE' ? 0.9 : 1;
      if (animation === 'RETREAT') {
        retreatFrom ??= { x: boss.x, y: boss.y };
        const padX = retreatFrom.x + RETREAT_PAD_AHEAD;
        jefe2.parade.visible = true;
        jefe2.parade.position.set(padX, GROUND_Y, -0.5);
        const floorTop = GROUND_Y + 0.1 + HOOF_OFFSET;
        const t = easeOut(retreatProgress(boss.retreatLeft, RETREAT_TICKS));
        jefe2.root.position.set(retreatFrom.x + (padX - retreatFrom.x) * t, retreatFrom.y + (floorTop - retreatFrom.y) * t, 0);
      } else {
        jefe2.parade.visible = false;
        jefe2.root.position.set(boss.x, boss.y + (steady ? 0 : Math.sin(seconds * 4) * 0.05), 0);
      }
      wallPool.update(run.attacks.objects.filter((object) => object.wall), core.tick);
    }
    if (jefe3 && boardPool && run.boss && run.attacks) {
      const boss = run.boss;
      const { rings } = run.attacks;
      if (rings.length > seenRings) lastRingTick = rings[rings.length - 1]!;
      seenRings = rings.length;
      if (boss.status === 'entering') retreatFrom = null;
      const sinceRing = lastRingTick === null ? null : core.tick - lastRingTick;
      const animation = jefe3Animation({
        status: boss.status,
        phaseName: boss.def.phases[boss.phase - 1]?.name ?? '',
        ticksSinceHit: run.decoys?.lastHitTick == null ? null : core.tick - run.decoys.lastHitTick,
        ticksSinceRing: sinceRing,
      });
      const phone = phoneAnimation(sinceRing);
      const capo = capoAnimation(boss.status);
      jefe3View = { animation, phone, capo, visible: boss.status !== 'entering' };
      jefe3.root.visible = boss.status !== 'entering';
      jefe3.root.userData.pose = animation;
      jefe3.phone.userData.pose = phone;
      jefe3.capo.userData.pose = capo;
      // The handset vibrates while the phone rings (information, so also with reduced motion); the capo holds it to his ear,
      // dials, leans over the check or waves his arm while he argues in the retreat.
      jefe3.handset.position.x = phone === 'RING' ? Math.sin(core.tick * 2.5) * 0.04 : 0;
      jefe3.arm.rotation.z = animation === 'ON_THE_PHONE' ? 2.6 : animation === 'DIALING' ? 1.2 + Math.sin(seconds * 8) * 0.2 : animation === 'DEFLATE' ? 2.0 + Math.sin(seconds * 6) * 0.5 : animation === 'STAGGER' ? 2.4 : 0.4;
      jefe3.capo.rotation.z = animation === 'CHECK' ? -0.25 : 0;
      jefe3.root.rotation.z = animation === 'STAGGER' ? Math.sin(core.tick * 0.5) * 0.1 : animation === 'FLOUR_HIT' ? 0.15 : 0;
      if (animation === 'DEFLATE') {
        // The balloon loses air and the desk settles gently on the ground, with him still arguing: no crash, no wreck.
        retreatFrom ??= { x: boss.x, y: boss.y };
        const progress = retreatProgress(boss.retreatLeft, RETREAT_TICKS);
        jefe3.balloon.scale.setScalar(balloonScale(progress));
        jefe3.balloon.position.y = 0.9 - (1 - balloonScale(progress)) * 0.9;
        const landed = GROUND_Y + 1.25;
        jefe3.root.position.set(retreatFrom.x, retreatFrom.y + (landed - retreatFrom.y) * easeOut(progress), 0);
      } else {
        jefe3.balloon.scale.setScalar(1);
        jefe3.balloon.position.y = 0.9;
        jefe3.root.position.set(boss.x, boss.y + (steady ? 0 : Math.sin(seconds * 3) * 0.06), 0);
      }
      boardPool.update(run.attacks);
    }
    if (jefe4 && run.boss && run.attacks) {
      const boss = run.boss;
      const chato = run.attacks.chato;
      if (boss.status === 'entering') retreatFrom = null;
      const animation = jefe4Animation({
        status: boss.status,
        chato: chato?.state ?? null,
        ticksSinceHit: run.decoys?.lastHitTick == null ? null : core.tick - run.decoys.lastHitTick,
      });
      jefe4View = { animation, visible: boss.status !== 'entering' && isFigureVisible(animation) };
      const mouthTunnel = chato?.tunnel ?? 'floor';
      const atCeiling = mouthTunnel === 'ceiling';
      // The trapdoors sit at the mouths of the tunnels at 80 % of the view; the one in use opens while he warns, is out and hides.
      const using = chato !== null && (chato.state === 'WARNING' || chato.state === 'OUT' || chato.state === 'HIDING');
      jefe4.trapdoorFloor.position.set(boss.x, GROUND_Y + 0.06, 0.2);
      jefe4.trapdoorCeiling.position.set(boss.x, WORLD_TOP - 0.06, 0.2);
      jefe4.trapdoorFloor.userData.pose = using && mouthTunnel === 'floor' ? 'open' : 'closed';
      jefe4.trapdoorCeiling.userData.pose = using && atCeiling ? 'open' : 'closed';
      jefe4.trapdoorFloor.visible = boss.status !== 'entering';
      jefe4.trapdoorCeiling.visible = boss.status !== 'entering';
      // The dust of the warning swells, or stays a static cloud with reduced motion (#139); the horizontal tunnel of the
      // motorbike warns at the right edge of the view, on the ground lane.
      jefe4.dust.visible = animation === 'warning';
      if (animation === 'warning') {
        const horizontal = mouthTunnel === 'horizontal';
        jefe4.dust.position.set(horizontal ? viewOf(run).right - 0.5 : boss.x, horizontal ? 2 : atCeiling ? WORLD_TOP - 0.5 : GROUND_Y + 0.5, 0.3);
        jefe4.dust.scale.setScalar(steady ? 1 : 0.8 + 0.2 * Math.sin(seconds * 6));
      }
      jefe4.root.visible = jefe4View.visible;
      jefe4.root.userData.pose = animation;
      jefe4.moto.visible = animation === 'moto' || (animation === 'hit' && chato?.state === 'MOTO') || (animation === 'stagger' && chato?.state === 'MOTO');
      jefe4.cell.visible = false;
      jefe4.root.rotation.z = animation === 'stagger' ? Math.sin(core.tick * 0.5) * 0.1 : animation === 'hit' ? 0.15 : 0;
      jefe4.root.scale.setScalar(1);
      jefe4.root.position.z = 0;
      jefe4.arms.forEach((arm, at) => (arm.rotation.z = (at === 0 ? 1 : -1) * (animation === 'out' ? -0.6 + (steady ? 0 : Math.sin(seconds * 5) * 0.25) : animation === 'hit' ? 0.9 : 0)));
      if (animation === 'retreat') {
        // The retreat: he goes into the tunnel and shows up arguing in his own cell, in the middle plane (no violence, no wreck).
        retreatFrom ??= { x: boss.x, y: boss.y };
        const progress = retreatProgress(boss.retreatLeft, RETREAT_TICKS);
        jefe4.root.userData.enteredTunnel = progress >= 0.3;
        jefe4.root.userData.pose = 'retreat';
        if (retreatPhase(progress) === 'tunnel') {
          jefe4.root.position.set(retreatFrom.x, retreatFrom.y + (GROUND_Y - 1 - retreatFrom.y) * (progress / 0.3), 0);
          jefe4.root.rotation.z = retreatFrom.y > 6 ? Math.PI : 0;
          jefe4.root.visible = true;
        } else {
          jefe4.cell.visible = true;
          jefe4.cell.position.set(retreatFrom.x + 5, 6, -15);
          jefe4.root.position.set(retreatFrom.x + 5, 5.2, -14.4);
          jefe4.root.rotation.z = 0;
          jefe4.root.scale.setScalar(0.6);
          jefe4.arms.forEach((arm, at) => (arm.rotation.z = (at === 0 ? 1 : -1) * (steady ? -0.5 : -0.5 + Math.sin(seconds * 8) * 0.5)));
          jefe4.root.visible = true;
        }
        jefe4View = { animation, visible: true };
      } else if (animation === 'moto' || jefe4.moto.visible) {
        jefe4.moto.position.set(boss.x, boss.y - 0.9, 0);
        jefe4.root.position.set(boss.x, boss.y + 0.5, 0.1);
      } else {
        jefe4.root.position.set(boss.x, boss.y, 0.1);
        if (atCeiling && chato?.state === 'OUT') jefe4.root.rotation.z = Math.PI;
      }
    }
    if (attackPool && run.attacks) attackPool.update(run.attacks.objects);
    if (bossLaw && run.attacks) {
      const helicopter = run.attacks.helicopter;
      const { left } = viewOf(run);
      placeHelicopter(bossLaw, { x: left + JEFE_1.helicopterFromView * VIEW_WIDTH, y: JEFE_1.helicopterY + (helicopter?.arrivalLeft ?? 0) }, helicopter ?? NO_HELICOPTER, helicopter !== null);
    }
    for (const [index, model] of craneModels) {
      const enemy = run.enemies[index]!;
      const view = craneView(enemy.crane!.ticks, run.level.enemies[index]!.params);
      model.group.visible = active.has(index);
      model.arm.rotation.z = toRadians(view.angle);
      model.setBeacon(view.beacon);
    }
    for (const [index, model] of hitmanModels) {
      const enemy = run.enemies[index]!;
      model.group.position.set(enemy.x, enemy.y, 0);
      // Only once it has taken off (it waits off screen before) and while it takes part in the simulation.
      model.group.visible = enemy.launched && active.has(index);
      model.propeller.rotation.x += 0.7;
    }
    for (const checkpoint of run.checkpoints.checkpoints) {
      flags.get(checkpoint.id)!.material = checkpoint.flare === 'green' ? materials.flagGreen : materials.flagRed;
    }
    overlay?.update({ x, y });
  };

  const record = () => {
    const objects: TraceEntry['objects'] = {};
    for (const name of TRACKED) {
      const object = scene.getObjectByName(name)!;
      objects[name] = { visible: object.visible, y: object.getWorldPosition(vector).y };
    }
    trace.push({ tick: run.core.tick, state: run.core.levelState, objects });
    if (trace.length > 600) trace.shift();
  };
  const vector = new Vector3();

  const maxFrame = { calls: 0, triangles: 0 };
  const markers = new Map<number, { calls: number; triangles: number }>();
  let nextMarker = 0;
  const render = (alpha: number) => {
    applyView(alpha);
    // The propeller spins with render time, apart from the simulation and from reduced motion (not a flash).
    const now = performance.now();
    chiva.parts.propeller.rotation.x += ((now - lastRender) / 1000) * PROPELLER_RAD_PER_S;
    lastRender = now;
    renderer.info.reset();
    renderer.render(scene, camera);
    const { calls, triangles } = renderer.info.render;
    maxFrame.calls = Math.max(maxFrame.calls, calls);
    maxFrame.triangles = Math.max(maxFrame.triangles, triangles);
    for (const maximum of markers.values()) {
      maximum.calls = Math.max(maximum.calls, calls);
      maximum.triangles = Math.max(maximum.triangles, triangles);
    }
  };

  const summary = (): Pick<RenderInfo, 'theme' | 'themeTransition' | 'memory'> => {
    const themeInfo = theme.info();
    return {
      theme: {
        id: themeInfo.id,
        variant: themeInfo.variant,
        sky: themeInfo.sky,
        fog: themeInfo.fog,
        background: (scene.background as { getHex(): number }).getHex(),
        sceneFog: scene.fog ? (scene.fog as { color: { getHex(): number } }).color.getHex() : null,
      },
      themeTransition: themeTransition ? { from: themeTransition.from.info().variant, ticksLeft: themeTransition.total - themeTransition.elapsed } : null,
      memory: { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures },
    };
  };
  const info = (): RenderInfo => {
    const objects = collectRenderedObjects(scene);
    for (const object of objects) addedNames.add(object.name);
    let sceneObjectCount = 0;
    scene.traverse(() => { sceneObjectCount += 1; });
    return {
      ...view.info(), ...summary(), objects, trace: [...trace],
      drawCalls: renderer.info.render.calls, triangles: renderer.info.render.triangles,
      frame: { calls: renderer.info.render.calls, triangles: renderer.info.render.triangles },
      maxFrame: { ...maxFrame }, sceneObjectCount,
      columnBoundsChecks: columns.validation(), decorationCounts: theme.info().decorationCounts,
      boss: jefe2View ? { animation: jefe2View.animation, visible: jefe2View.visible } : jefe3View ? { animation: jefe3View.animation, visible: jefe3View.visible, phone: jefe3View.phone, capo: jefe3View.capo } : jefe4View ? { animation: jefe4View.animation, visible: jefe4View.visible } : null,
      layerScreenX: { far: theme.layerScreenX('layer.far', camera.position.x), mid: theme.layerScreenX('layer.mid', camera.position.x) },
      cameraShakes, flashes: 0, addedNames: [...addedNames].sort(),
    };
  };

  // Upload fixed resources before flight. Restore visibility and active ids synchronously, then draw
  // the actual first frame so the preload frame is never presented as gameplay.
  columns.warmup();
  run.level.pickups.forEach((pickup, index) => {
    if (pickup.type !== 'dollar') return;
    dollarMatrix.makeTranslation(pickup.x, pickup.y, 0);
    dollars.set(`dollar:${index}`, dollarMatrix);
  });
  const savedVisibility: { object: import('three').Object3D; visible: boolean; culled: boolean }[] = [];
  scene.traverse((object) => {
    savedVisibility.push({ object, visible: object.visible, culled: object.frustumCulled });
    object.visible = true;
    object.frustumCulled = false;
  });
  renderer.render(scene, camera);
  for (const { object, visible, culled } of savedVisibility) {
    object.visible = visible;
    object.frustumCulled = culled;
  }
  render(1);

  return {
    canvas: renderer.domElement,
    get hitboxesOn() {
      return overlay !== null;
    },
    beforeStep() {
      previous = { x: run.core.player.x, y: run.core.player.y, vy: run.core.player.vy };
    },
    afterStep() {
      swing = stepBagSwing(swing, 1 / SIM_HZ, run.core.player.vy - previous.vy);
      // Crossing the border switches to the US sky theme (GDD §11.1, #97), once.
      if (!sawUsSky && run.usSky) {
        sawUsSky = true;
        const usSky = usSkyThemeFor(run.level.def.world);
        if (usSky) beginThemeTransition(usSky);
      }
      stepThemeTransition();
      applyView(1);
      record();
    },
    render,
    setHitboxes,
    requestTheme,
    coverage: columns.coverage,
    columnParts: columns.parts,
    info,
    summary,
    markFrame() {
      const marker = nextMarker++;
      markers.set(marker, { calls: 0, triangles: 0 });
      return marker;
    },
    maxSince(marker) {
      const maximum = markers.get(marker);
      if (!maximum) throw new Error(`Unknown render marker ${marker}`);
      return { ...maximum };
    },
    dispose() {
      view.dispose();
      setHitboxes(false);
      chiva.dispose();
      fx.dispose();
      jefe1?.dispose();
      jefe2?.dispose();
      wallPool?.dispose();
      jefe3?.dispose();
      boardPool?.dispose();
      jefe4?.dispose();
      jumpProps.dispose();
      attackPool?.dispose();
      bossLaw?.dispose();
      powerUps.dispose();
      birdPrototype?.dispose();
      seagulls.dispose();
      for (const model of antennaModels.values()) {
        model.antenna.dispose();
        model.hitman.dispose();
      }
      for (const model of hitmanModels.values()) model.dispose();
      for (const model of craneModels.values()) model.dispose();
      for (const model of helicopterModels.values()) model.dispose();
      for (const model of paragliderModels.values()) model.dispose();
      for (const model of hippoModels.values()) model.dispose();
      for (const model of dusterModels.values()) model.dispose();
      for (const model of cableCarModels.values()) model.dispose();
      for (const geometry of geometries) geometry.dispose();
      dollarMesh.dispose();
      for (const material of Object.values(materials) as Material[]) material.dispose();
      columns.dispose();
      theme.dispose();
      releaseRenderer(renderer);
      renderer.domElement.remove();
    },
  };
}
